import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSession, useTenant } from '../../auth/session';
import { useCompanyPractice } from '../../pages/team/Accountant';
import { BRAND } from '../../lib/brand';
import type { HelpView } from '../../lib/help';
import {
  ask,
  firstQuestion,
  giveFeedback,
  parseAnswer,
  startConversation,
  useAssistantUsage,
  useConversation,
  type AssistantMode,
  type ChatMessage,
  type Inline,
} from './assistant';

// The assistant's chat view in the help panel (APP-DESIGN.md 11.2). Answers stream in; until the
// first words arrive there is only a quiet "Thinking…" line.

function InlineText({ parts }: { parts: Inline[] }) {
  return <>{parts.map((p, i) => {
    if (p.kind === 'bold') return <b key={i}>{p.text}</b>;
    if (p.kind === 'link') return p.internal ? <Link key={i} to={p.href}>{p.text}</Link> : <a key={i} href={p.href} target="_blank" rel="noreferrer">{p.text}</a>;
    return <span key={i}>{p.text}</span>;
  })}</>;
}

function Answer({ text }: { text: string }) {
  return <>{parseAnswer(text).map((b, i) => {
    if (b.kind === 'p') return <p key={i}><InlineText parts={b.parts} /></p>;
    const items = b.items.map((it, j) => <li key={j}><InlineText parts={it} /></li>);
    return b.kind === 'ol' ? <ol key={i}>{items}</ol> : <ul key={i}>{items}</ul>;
  })}</>;
}

export function AssistantView({ question, mode: askedMode, onView }: { question?: string | undefined; mode?: AssistantMode | undefined; onView: (v: HelpView) => void }) {
  const session = useSession();
  const tenant = useTenant();
  const location = useLocation();
  const queryClient = useQueryClient();
  const usage = useAssistantUsage();
  const practice = useCompanyPractice();
  const owner = `${session.user?.email ?? 'dev'}:${tenant.id}`;
  const conversation = useConversation(owner);
  const [chosen, setMode] = useState<AssistantMode>(askedMode ?? conversation?.mode ?? 'help');
  const [draft, setDraft] = useState('');
  const asked = useRef(false);
  const end = useRef<HTMLDivElement>(null);
  // Help answers need the Help Center; without it only questions about the books are open.
  const helpOpen = usage.data?.help ?? true;
  const booksOpen = usage.data?.books ?? false;
  const mode: AssistantMode = !helpOpen && booksOpen ? 'books' : !booksOpen ? 'help' : chosen;
  const messages = conversation && conversation.mode === mode ? conversation.messages : [];
  const busy = messages.some((m) => m.pending);
  const linked = (practice.data ?? []).find((p) => !p.via_practice && (p.state === 'active' || p.state === 'practice_managed'));

  const send = (q: string) => {
    const text = q.trim();
    if (!text || busy) return;
    setDraft('');
    void ask(owner, mode, text, location.pathname).then(() => queryClient.invalidateQueries({ predicate: (k) => k.queryKey[1] === 'support' && k.queryKey[2] === 'assistant-usage' }));
  };
  // A question from the home view or ⌘K is asked once, on opening.
  useEffect(() => {
    if (asked.current || !question?.trim()) return;
    asked.current = true;
    send(question);
  }, [question]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [messages.length, messages.at(-1)?.text, messages.at(-1)?.activity]);

  const switchMode = (m: AssistantMode) => { if (m !== mode && !busy) { setMode(m); startConversation(owner, m); } };
  const handover = (m: ChatMessage | undefined) => {
    if (m?.messageId) void giveFeedback(m.messageId, 'handover');
    onView({ kind: 'form', type: 'howto', prefill: { subject: firstQuestion(conversation).slice(0, 200), body: '', ...(conversation?.id ? { assistantConversationId: conversation.id } : {}) } });
  };

  const intro: ReactNode = mode === 'help'
    ? `Answers come from ${BRAND.name} help articles. It can make mistakes, so check the linked articles.`
    : `Answers come from this company's books, read with your own access. It can't change anything, and it can make mistakes, so check the linked figures.`;

  return (
    <div className="assistant">
      <div className="assistant-intro">
        <span className="assistant-ai" aria-label="AI">AI</span>
        <p>{intro}</p>
      </div>
      {booksOpen && helpOpen && (
        <div className="grp-modes assistant-modes" role="radiogroup" aria-label="Ask about">
          <button type="button" role="radio" aria-checked={mode === 'help'} className={`seg-btn ${mode === 'help' ? 'on' : ''}`} onClick={() => switchMode('help')} disabled={busy}>Using {BRAND.name}</button>
          <button type="button" role="radio" aria-checked={mode === 'books'} className={`seg-btn ${mode === 'books' ? 'on' : ''}`} onClick={() => switchMode('books')} disabled={busy}>Your books</button>
        </div>
      )}

      <div className="assistant-thread" aria-live="polite">
        {messages.length === 0 && (
          <p className="help-muted">{mode === 'help' ? 'Ask how to do something, for example "How do I reconcile my bank account?"' : 'Ask about your books, for example "Who owes me the most?" or "Which bank lines still need matching?"'}</p>
        )}
        {messages.map((m, i) => m.role === 'user'
          ? <div key={i} className="assistant-q">{m.text}</div>
          : (
            <div key={i} className="assistant-a">
              {m.text ? <Answer text={m.text} /> : m.pending && <p className="assistant-thinking">{m.activity ?? 'Thinking…'}</p>}
              {m.pending && m.text && m.activity && <p className="assistant-thinking">{m.activity}…</p>}
              {m.error && (
                <p className="help-error" role="alert">{m.error.message}</p>
              )}
              {!m.pending && m.taxNote && (
                <p className="assistant-note">Tax decisions, like whether VAT can be claimed, are for your accountant.{linked && <> <Link to="/from-your-accountant">Ask {linked.practice_name}</Link></>}</p>
              )}
              {!m.pending && !m.error && (m.articles?.length ?? 0) > 0 && (
                <p className="assistant-from">From: {m.articles!.map((a, j) => (
                  <span key={a.id}>{j > 0 && ', '}<button type="button" className="link-btn" onClick={() => onView({ kind: 'article', id: a.id })}>{a.title}</button></span>
                ))}</p>
              )}
              {!m.pending && (
                <div className="assistant-actions">
                  {m.error || m.noAnswer ? (
                    <button type="button" className="btn small" onClick={() => handover(m)}>Talk to a person</button>
                  ) : m.feedback === 'solved' ? (
                    <span className="help-muted">Glad it helped.</span>
                  ) : (
                    <>
                      <button type="button" className="btn small" onClick={() => m.messageId && void giveFeedback(m.messageId, 'solved')}>That solved it</button>
                      <button type="button" className="btn small" onClick={() => handover(m)}>Talk to a person</button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        <div ref={end} />
      </div>

      <form className="assistant-foot" onSubmit={(e) => { e.preventDefault(); send(draft); }}>
        <div className="assistant-input">
          <span className="assistant-mark" aria-hidden="true">✦</span>
          <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={messages.length ? 'Ask a follow-up…' : 'Ask a question…'} aria-label="Ask the assistant" maxLength={4000} data-autofocus disabled={busy} />
          <button type="submit" className="btn primary small" disabled={busy || !draft.trim()}>Ask</button>
        </div>
        <p className="help-muted">Talk to a person sends this conversation to our team, so you don't have to repeat it.</p>
      </form>
    </div>
  );
}
